import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { safeStorage } from '@shared/lib/storage';

import { isThemeMode, THEME_STORAGE_KEY, type ThemeMode } from './theme';

const RAIL_STORAGE_KEY = 'cd-rail-hidden';
const YEAR_STORAGE_KEY = 'cd-year';

export interface UiState {
  themeMode: ThemeMode;
  railCollapsed: boolean;
  /** Calendar (processing) year the user is working in; null = the current year. */
  processingYear: number | null;
  /** Inbox tabs run edge to edge (no sidebar or masthead); Esc leaves. Not persisted. */
  fullscreen: boolean;
}

function initialState(): UiState {
  const stored = safeStorage.get(THEME_STORAGE_KEY);
  return {
    themeMode: isThemeMode(stored) ? stored : 'auto',
    railCollapsed: safeStorage.get(RAIL_STORAGE_KEY) === '1',
    processingYear: Number(safeStorage.get(YEAR_STORAGE_KEY)) || null,
    fullscreen: false,
  };
}

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setThemeMode(state, action: PayloadAction<ThemeMode>) {
      state.themeMode = action.payload;
    },
    toggleRail(state) {
      state.railCollapsed = !state.railCollapsed;
    },
    setProcessingYear(state, action: PayloadAction<number>) {
      state.processingYear = action.payload;
    },
    setFullscreen(state, action: PayloadAction<boolean>) {
      state.fullscreen = action.payload;
    },
  },
  selectors: {
    selectThemeMode: (s) => s.themeMode,
    selectRailCollapsed: (s) => s.railCollapsed,
    selectStoredYear: (s) => s.processingYear,
    selectFullscreen: (s) => s.fullscreen,
  },
});

export const { setThemeMode, toggleRail, setProcessingYear, setFullscreen } = uiSlice.actions;
export const { selectThemeMode, selectRailCollapsed, selectStoredYear, selectFullscreen } = uiSlice.selectors;

/** Persist the per-browser UI choices (Phase 1 also saves theme to /preferences). */
export function persistUi(state: UiState): void {
  safeStorage.set(THEME_STORAGE_KEY, state.themeMode);
  safeStorage.set(RAIL_STORAGE_KEY, state.railCollapsed ? '1' : '0');
  if (state.processingYear) safeStorage.set(YEAR_STORAGE_KEY, String(state.processingYear));
}
